import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getDocumentView,
  downloadDocumentVersion,
  downloadDocumentVersionBlob,
  streamDocumentVersionBlob,
  getFileExtensionFromContentType,
} from '../../endpoints/documents';
import { processLinkUrl } from '../../utils/urlUtils';
import VideoPlayer from '../../Components/VideoPlayer';
import AlertItem from '../../Components/AlertItem';

const DocumentView: React.FC = () => {
  const navigate = useNavigate();
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [viewerError, setViewerError] = useState<string>('');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isViewerLoading, setIsViewerLoading] = useState<boolean>(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  // Fetch document data
  const {
    data: document,
    isLoading,
    error,
  } = useApiGet(
    ['document-view', id, versionId],
    () => getDocumentView(id!, versionId!),
    {
      enabled: !!id && !!versionId,
    }
  );

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  // Add keyboard shortcut prevention for read-only documents
  useEffect(() => {
    if (!document?.read_only) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only prevent critical shortcuts when document viewer is focused
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'p' || // Print
          e.key === 's' || // Save
          e.key === 'a' || // Select All
          e.key === 'c' || // Copy
          e.key === 'v' || // Paste
          e.key === 'x') // Cut
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleBeforePrint = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    window.document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('beforeprint', handleBeforePrint, true);

    // Cleanup
    return () => {
      window.document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('beforeprint', handleBeforePrint, true);
    };
  }, [document?.read_only]);

  // Function to detect document type and determine viewing method
  const getDocumentType = (
    url: string,
    contentType?: string
  ): 'pdf' | 'image' | 'office' | 'text' | 'other' => {
    const urlLower = url.toLowerCase();

    // Check content type first (more reliable)
    if (contentType) {
      const contentTypeLower = contentType.toLowerCase();
      if (contentTypeLower.includes('application/pdf')) return 'pdf';
      if (contentTypeLower.includes('image/')) return 'image';
      if (
        contentTypeLower.includes(
          'application/vnd.openxmlformats-officedocument'
        ) ||
        contentTypeLower.includes('application/msword') ||
        contentTypeLower.includes('application/vnd.ms-')
      )
        return 'office';
      if (contentTypeLower.includes('text/')) return 'text';
    }

    // Fallback to URL extension detection
    if (urlLower.includes('.pdf')) return 'pdf';
    if (
      urlLower.includes('.jpg') ||
      urlLower.includes('.jpeg') ||
      urlLower.includes('.png') ||
      urlLower.includes('.gif') ||
      urlLower.includes('.webp') ||
      urlLower.includes('.svg')
    )
      return 'image';
    if (
      urlLower.includes('.docx') ||
      urlLower.includes('.doc') ||
      urlLower.includes('.pptx') ||
      urlLower.includes('.ppt') ||
      urlLower.includes('.xlsx') ||
      urlLower.includes('.xls')
    )
      return 'office';
    if (
      urlLower.includes('.txt') ||
      urlLower.includes('.html') ||
      urlLower.includes('.css') ||
      urlLower.includes('.js') ||
      urlLower.includes('.json') ||
      urlLower.includes('.xml')
    )
      return 'text';

    return 'other';
  };

  const handleOpenDocument = async () => {
    try {
      setViewerError('');

      if (document?.read_only) {
        const blobResponse = await streamDocumentVersionBlob(
          id!,
          versionId || 'current'
        );

        const newObjectUrl = URL.createObjectURL(
          new Blob([blobResponse.blob], {
            type: blobResponse.contentType || 'application/pdf',
          })
        );

        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }

        setObjectUrl(newObjectUrl);
        setDocumentUrl(newObjectUrl);
        setPdfUrl(newObjectUrl);
        setShowPdfViewer(true);
        setIsViewerLoading(true);
        return;
      }

      // For non-read-only documents, use the existing logic
      // Try to get a viewing URL first, fallback to blob if needed
      let viewUrl: string;
      let documentType: string;

      try {
        // First try: Get presigned URL for viewing
        const response = await downloadDocumentVersion(
          id!,
          versionId || 'current'
        );
        viewUrl = response.url;
        setDocumentUrl(viewUrl); // Store the actual document URL
        documentType = getDocumentType(viewUrl);

        // If the URL looks like it's for downloading, try blob approach
        if (
          viewUrl.includes('response-content-disposition=attachment') ||
          viewUrl.includes('response-content-disposition%3Dattachment')
        ) {
          throw new Error('URL configured for download, trying blob approach');
        }
      } catch (error) {
        console.log('Presigned URL approach failed, trying blob approach...');

        // Fallback: Use blob approach for viewing
        const blobResponse = await downloadDocumentVersionBlob(
          id!,
          versionId || 'current'
        );

        // Create object URL from blob with proper content type
        const newObjectUrl = URL.createObjectURL(
          new Blob([blobResponse.blob], {
            type: blobResponse.contentType || 'application/pdf',
          })
        );

        // Clean up previous object URL if it exists
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }

        setObjectUrl(newObjectUrl);
        viewUrl = newObjectUrl;
        setDocumentUrl(viewUrl); // Store the actual document URL (blob)
        documentType = getDocumentType(viewUrl, blobResponse.contentType);
      }

      switch (documentType) {
        case 'pdf':
          // For PDFs, use Google Docs Viewer as default (most reliable)
          const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(viewUrl)}&embedded=true`;
          setIsViewerLoading(true);
          setPdfUrl(googleDocsUrl);
          setShowPdfViewer(true);

          // Set a timeout to detect if PDF fails to load
          setTimeout(() => {
            if (isViewerLoading) {
              setViewerError(
                'PDF took too long to load. Try opening in a new tab or downloading the document.'
              );
              setIsViewerLoading(false);
            }
          }, 15000); // 15 second timeout for Google Docs
          break;
        case 'image':
          // Open images in embedded viewer (better than new tab)
          setPdfUrl(viewUrl);
          setShowPdfViewer(true);
          break;
        case 'office':
          // Try Microsoft Office Online first, then Google Docs Viewer
          try {
            // Microsoft Office Online (better for Office docs)
            const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(viewUrl)}`;
            setPdfUrl(officeUrl);
            setShowPdfViewer(true);
          } catch (error) {
            // Fallback: Google Docs Viewer
            const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(viewUrl)}&embedded=true`;
            setPdfUrl(googleDocsUrl);
            setShowPdfViewer(true);
          }
          break;
        case 'text':
          // Text files can be opened in embedded viewer
          setPdfUrl(viewUrl);
          setShowPdfViewer(true);
          break;
        default:
          // Other document types open in new tab
          window.open(viewUrl, '_blank');
          break;
      }
    } catch (error) {
      console.error('Error opening document:', error);
      setViewerError(
        document?.read_only
          ? 'Failed to open document. Please try again or use the PEO Portal.'
          : 'Failed to open document. Please try again or download the document.'
      );
    }
  };

  const handleFallbackToDownload = () => {
    // Open the document URL in a new tab
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const handleRetryWithGoogleDocs = () => {
    if (documentUrl) {
      const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(documentUrl)}&embedded=true`;
      setPdfUrl(googleDocsUrl);
      setViewerError(''); // Clear error
    } else {
      setViewerError('No document URL available for Google Docs fallback');
    }
  };

  const handleTryDirectView = () => {
    // Use blob URL if available, otherwise use document URL
    const directUrl = objectUrl || documentUrl;
    if (directUrl) {
      setPdfUrl(directUrl);
      setViewerError(''); // Clear error
      setIsViewerLoading(true);
    } else {
      setViewerError('No URL available for direct view');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadDocumentVersionBlob(
        id!,
        versionId || 'current'
      );

      // Determine the filename with proper extension
      let filename = response.filename;
      if (!filename) {
        // Fallback to document name if no filename from API
        const baseName = document?.name || 'document';
        const extension = response.contentType
          ? getFileExtensionFromContentType(response.contentType)
          : '';
        filename = baseName + extension;
      }

      // Create a download link
      const url = window.URL.createObjectURL(response.blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleCopyLink = () => {
    if (id) {
      const link = `${window.location.origin}/library/document/${id}/latest`;
      navigator.clipboard
        .writeText(link)
        .then(() => {
          console.log('Link copied:', link);
          alert('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link:', err);
          // Fallback: show the link in an alert
          alert(`Link: ${link}`);
        });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading document...</Typography>
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading document"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 14 }}>
      {/* CSS for read-only document text selection prevention */}
      {document?.read_only && (
        <style>
          {`
            .read-only-document {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            .read-only-document * {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            @media print {
              .read-only-document {
                display: none !important;
              }
            }
            /* Note: PDF viewer controls are inside iframe, so we use sandbox attributes instead */
          `}
        </style>
      )}
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/library"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Typography variant="body2" color="text.primary">
          {document.name}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            {document.name}
          </Typography>
          <Chip
            label={`v${document.version}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Document Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Document Information
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {document.description ||
                  document.document_description ||
                  'No description available'}
              </Typography>

              {document.owner && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Owner: {document.owner}
                </Typography>
              )}

              {/* Link URL - if it exists */}
              {document.link_url && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
                >
                  <Link
                    href={processLinkUrl(document.link_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontWeight: 'medium',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {document.name}
                  </Link>
                  <OpenInNewIcon color="primary" fontSize="small" />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                {document.downloadable && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={handleOpenDocument}
                    sx={{
                      color: '#FF6B35',
                      borderColor: '#FF6B35',
                      '&:hover': {
                        borderColor: '#E55A2B',
                        backgroundColor: 'rgba(255, 107, 53, 0.04)',
                      },
                    }}
                  >
                    View Document
                  </Button>
                )}
                {document.downloadable && !document.read_only && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{
                      backgroundColor: '#00A859',
                      '&:hover': {
                        backgroundColor: '#008A4A',
                      },
                    }}
                  >
                    Download
                  </Button>
                )}
                {document.read_only && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This document is read-only and cannot be downloaded.
                  </Alert>
                )}
                {document.link_url && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    href={processLinkUrl(document.link_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#FF6B35',
                      borderColor: '#FF6B35',
                      '&:hover': {
                        borderColor: '#E55A2B',
                        backgroundColor: 'rgba(255, 107, 53, 0.04)',
                      },
                    }}
                  >
                    Go to
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  sx={{
                    color: '#6C63FF',
                    borderColor: '#6C63FF',
                    '&:hover': {
                      borderColor: '#5A52E0',
                      backgroundColor: 'rgba(108, 99, 255, 0.04)',
                    },
                  }}
                >
                  Copy Link
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Document Bundles */}
          {document.bundles.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Document Bundles
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This document is part of the following bundles:
                </Typography>
                <Stack spacing={1}>
                  {document.bundles.map((bundle) => (
                    <Card
                      key={bundle.id}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-1px)',
                        },
                        border: '1px solid #e0e0e0',
                      }}
                      onClick={() => navigate(`/library/bundles/${bundle.id}`)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 'medium', flexGrow: 1 }}
                          >
                            {bundle.name}
                          </Typography>
                          <OpenInNewIcon
                            color="primary"
                            fontSize="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {bundle.description || 'No description'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{
                            display: 'block',
                            mt: 1,
                            fontWeight: 'medium',
                          }}
                        >
                          Click to view bundle details
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Video Content - Full width below main content */}
      {document.video_url && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ mb: 3, pt: 3, height: '50vh' }}>
              <VideoPlayer
                width="100%"
                height="100%"
                videoUrl={document.video_url}
                title=""
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Related Documents - Note: This section needs to be updated based on the actual API response */}
      {/* The DocumentView type doesn't include bundle documents, so this section is commented out for now */}
      {/*
      {document.bundles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" gutterBottom>
            Related Documents
          </Typography>
          <Grid container spacing={2}>
            {document.bundles.flatMap((bundle) =>
              bundle.documents
                .filter((doc) => doc.document_id !== document.id)
                .map((doc) => (
                  <Grid item xs={12} md={4} key={doc.id}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                        >
                          {getFileIcon(doc)}
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{ ml: 1, flexGrow: 1 }}
                          >
                            {doc.name}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {doc.description || 'No description'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {dayjs(doc.created_at).format('MM/DD/YYYY')}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewIcon />}
                          >
                            View
                          </Button>
                          {doc.downloadable && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                            >
                              Download
                            </Button>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
            )}
          </Grid>
        </Box>
      )}
      */}

      {/* PDF Viewer Dialog */}
      <Dialog
        open={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          // Clean up object URL when viewer is closed
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            setObjectUrl(null);
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Document Viewer
          <IconButton onClick={() => setShowPdfViewer(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {pdfUrl && (
            <Box
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                {!document?.read_only && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(pdfUrl, '_blank')}
                      sx={{ mr: 2 }}
                    >
                      Direct PDF View
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() =>
                        window.open(
                          `https://docs.google.com/gview?url=${encodeURIComponent(documentUrl || pdfUrl)}&embedded=true`,
                          '_blank'
                        )
                      }
                      sx={{ mr: 2 }}
                    >
                      Google Docs Viewer
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                    >
                      Download
                    </Button>
                  </>
                )}
                {document?.read_only && (
                  <Alert severity="info" sx={{ mb: 0 }}>
                    This document is read-only. Viewing only - no download or
                    external access allowed.
                  </Alert>
                )}
              </Box>
              <Box
                sx={{ flex: 1, position: 'relative' }}
                className={document?.read_only ? 'read-only-document' : ''}
              >
                {viewerError ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Box sx={{ mb: 2, maxWidth: 500 }}>
                      <AlertItem
                        severity="error"
                        description={viewerError}
                        onClose={() => {}}
                        keepOpen={true}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleOpenDocument}
                      startIcon={<ViewIcon />}
                      sx={{ mr: 2 }}
                    >
                      Try Again
                    </Button>
                    {!document?.read_only && (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleRetryWithGoogleDocs}
                          startIcon={<OpenInNewIcon />}
                          sx={{ mr: 2 }}
                        >
                          Retry Google Docs Viewer
                        </Button>
                        {documentUrl && (
                          <Button
                            variant="outlined"
                            onClick={handleTryDirectView}
                            startIcon={<ViewIcon />}
                            sx={{ mr: 2 }}
                          >
                            Try Direct View
                          </Button>
                        )}
                      </>
                    )}
                    {!document?.read_only && (
                      <>
                        <Button
                          variant="outlined"
                          onClick={handleFallbackToDownload}
                          startIcon={<OpenInNewIcon />}
                          sx={{ mr: 2 }}
                        >
                          Open in New Tab
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleDownload}
                          startIcon={<DownloadIcon />}
                        >
                          Download Document
                        </Button>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    {isViewerLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1,
                          textAlign: 'center',
                        }}
                      >
                        <CircularProgress />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Loading document...
                        </Typography>
                      </Box>
                    )}
                    {pdfUrl.startsWith('blob:') ? (
                      // Use object tag for blob URLs (better PDF support)
                      <object
                        data={pdfUrl}
                        type="application/pdf"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        onContextMenu={(e) => {
                          if (document?.read_only) {
                            e.preventDefault();
                            return false;
                          }
                        }}
                        onLoad={() => {
                          setIsViewerLoading(false);
                        }}
                        onError={(e) => {
                          setIsViewerLoading(false);
                          setViewerError(
                            document?.read_only
                              ? 'Failed to load document in viewer. Please try again or use the PEO Portal.'
                              : 'Failed to load document in viewer. Try direct view or download the document.'
                          );
                        }}
                      >
                        <p>
                          Your browser does not support PDFs.
                          {!document?.read_only && (
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Click here to download the PDF
                            </a>
                          )}
                        </p>
                      </object>
                    ) : (
                      // Use iframe for external URLs (Google Docs, etc.)
                      <iframe
                        src={pdfUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="Document Viewer"
                        sandbox={
                          document?.read_only
                            ? 'allow-same-origin allow-scripts'
                            : undefined
                        }
                        onContextMenu={(e) => {
                          if (document?.read_only) {
                            e.preventDefault();
                            return false;
                          }
                        }}
                        onLoad={() => {
                          setIsViewerLoading(false);
                        }}
                        onError={(e) => {
                          setIsViewerLoading(false);
                          setViewerError(
                            document?.read_only
                              ? 'Failed to load document in viewer. Please try again or use the PEO Portal.'
                              : 'Failed to load document in viewer. Try direct view or download the document.'
                          );
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentView;
